import { ChangeEvent, MouseEvent, ReactNode, useRef } from 'react';
import { Image, XCircle } from 'react-feather';
import { useTranslation } from 'react-i18next';
import UI from '../UI';

interface UploadButtonProps {
  button: (props: { onClick: (event: MouseEvent<HTMLButtonElement>) => void; }) => ReactNode;
  onSelect: (files: FileList) => void;
  multiple?: boolean;
  accept?: string;
}

const UploadButton = ({ button, onSelect, multiple, accept }: UploadButtonProps) => {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;

    onSelect(files);

    if (inputRef.current) {
      inputRef.current.value = null;
    }
  };

  const handleButtonClick = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();

    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <>
      {button({ onClick: handleButtonClick })}
      <input
        ref={inputRef}
        type="file"
        hidden
        multiple={multiple}
        accept={accept}
        onChange={handleFileUpload}
        aria-label={t('forms.upload')}
      />
    </>
  );
};

interface ImageInputProps {
  value: File | null;
  deleteImage: boolean;
  onChange: (file: File | null) => unknown;
  previewImage?: string | null;
  imageAlt?: string;
  width?: number;
  ratio?: number;
  accept?: string;
}

export const ImageInput = ({
  value, deleteImage, onChange, previewImage, imageAlt, width = 200, ratio, accept = '.jpg,.jpeg,.png',
}: ImageInputProps) => {
  const { t } = useTranslation('common');

  return (
    <UploadButton
      button={({ onClick }) => (
        <>
          {(value || (previewImage && !deleteImage)) && (
            <UI.Div sc={{ mb: 2 }}>
              <UI.Card
                sc={{
                  outline: 'gray.200',
                  borderRadius: 'md',
                  spacing: 'xs',
                }}
                style={{ width }}
              >
                <UI.Image sc={{ ratio }}>
                  <div>
                    <img
                      src={value ? URL.createObjectURL(value) : previewImage}
                      alt={imageAlt}
                      style={{ borderRadius: 3 }}
                    />
                  </div>
                </UI.Image>
                <UI.Div sc={{ textAlign: 'right', mt: 1 }}>
                  <UI.Button
                    onClick={() => onChange(null)}
                    sc={{ blank: true, size: 'sm' }}
                  >
                    <UI.Icon>
                      <XCircle />
                    </UI.Icon>
                    {' '}
                    {t('forms.remove')}
                  </UI.Button>
                </UI.Div>
              </UI.Card>
            </UI.Div>
          )}
          {(!value && (!previewImage || deleteImage)) && (
            <UI.Button onClick={onClick} sc={{ outline: true, mr: 1 }}>
              <UI.Icon>
                <Image />
              </UI.Icon>
              {' '}
              {t('forms.choose')}
            </UI.Button>
          )}
        </>
      )}
      onSelect={(files) => onChange(files[0])}
      accept={accept}
    />
  );
};

export default UploadButton;
